import React, { useState } from 'react';
import { Lock, Eye, EyeOff, CheckCircle, Save, X, Loader2 } from 'lucide-react';
import { userService } from '../services/userService';
import toast from 'react-hot-toast';
import IconButton from '../components/IconButton';

const SpinLoader = (props) => <Loader2 className={`${props.className} animate-spin`} />;

const ChangePassword = () => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpar erro do campo quando usuário começar a digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.currentPassword) {
      newErrors.currentPassword = 'Senha atual é obrigatória';
    }
    
    if (!formData.newPassword) {
      newErrors.newPassword = 'Nova senha é obrigatória';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'Nova senha deve ter pelo menos 6 caracteres';
    } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.newPassword)) {
      newErrors.newPassword = 'Nova senha deve conter pelo menos uma letra minúscula, uma maiúscula e um número';
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Confirmação de senha é obrigatória';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Senhas não coincidem';
    }
    
    if (formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = 'A nova senha deve ser diferente da senha atual';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await userService.changePassword(
        formData.currentPassword,
        formData.newPassword
      );
      
      if (response.success) {
        setSuccess(true);
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        toast.success('Senha alterada com sucesso!');
      } else {
        if (response.message?.includes('Senha atual incorreta')) {
          setErrors({ currentPassword: 'Senha atual incorreta' });
        } else {
          setErrors({ general: response.message || 'Erro ao alterar senha' });
        }
      }
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      const message = error.response?.data?.message || 'Erro interno do servidor';
      
      if (message.includes('Senha atual incorreta')) {
        setErrors({ currentPassword: 'Senha atual incorreta' });
      } else {
        setErrors({ general: message });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setSuccess(false);
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setErrors({});
  };

  if (success) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="card p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Senha alterada com sucesso!
          </h2>
          <p className="text-gray-600 mb-6">
            Sua senha foi alterada com sucesso. Use a nova senha em seus próximos logins.
          </p>
          <div className="flex justify-center">
            <IconButton
              type="button"
              icon={Save}
              variant="primary"
              title="Alterar senha novamente"
              onClick={resetForm}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Alterar Senha</h1>
        <p className="text-gray-600 mt-1">
          Mantenha sua conta segura alterando sua senha regularmente.
        </p>
      </div>

      {/* Form */}
      <div className="card p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Erro geral */}
          {errors.general && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
              {errors.general}
            </div>
          )}

          {/* Senha atual */}
          <div>
            <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
              Senha atual
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="currentPassword"
                name="currentPassword"
                type={showPasswords.current ? 'text' : 'password'}
                value={formData.currentPassword}
                onChange={handleChange}
                className={`input pl-10 pr-10 ${errors.currentPassword ? 'border-red-300 focus:ring-red-500' : ''}`}
                placeholder="Digite sua senha atual"
              />
              <IconButton
                type="button"
                icon={showPasswords.current ? EyeOff : Eye}
                variant="info"
                size="sm"
                title={showPasswords.current ? 'Ocultar senha' : 'Mostrar senha'}
                onClick={() => togglePasswordVisibility('current')}
                className="absolute right-2 top-1/2 -translate-y-1/2"
              />
            </div>
            {errors.currentPassword && (
              <p className="mt-1 text-sm text-red-600">{errors.currentPassword}</p>
            )}
          </div>

          {/* Nova senha */}
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
              Nova senha
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="newPassword"
                name="newPassword"
                type={showPasswords.new ? 'text' : 'password'}
                value={formData.newPassword}
                onChange={handleChange}
                className={`input pl-10 pr-10 ${errors.newPassword ? 'border-red-300 focus:ring-red-500' : ''}`}
                placeholder="Digite sua nova senha"
              />
              <IconButton
                type="button"
                icon={showPasswords.new ? EyeOff : Eye}
                variant="info"
                size="sm"
                title={showPasswords.new ? 'Ocultar senha' : 'Mostrar senha'}
                onClick={() => togglePasswordVisibility('new')}
                className="absolute right-2 top-1/2 -translate-y-1/2"
              />
            </div>
            {errors.newPassword && (
              <p className="mt-1 text-sm text-red-600">{errors.newPassword}</p>
            )}
          </div>

          {/* Confirmar nova senha */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
              Confirmar nova senha
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showPasswords.confirm ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`input pl-10 pr-10 ${errors.confirmPassword ? 'border-red-300 focus:ring-red-500' : ''}`}
                placeholder="Confirme sua nova senha"
              />
              <IconButton
                type="button"
                icon={showPasswords.confirm ? EyeOff : Eye}
                variant="info"
                size="sm"
                title={showPasswords.confirm ? 'Ocultar senha' : 'Mostrar senha'}
                onClick={() => togglePasswordVisibility('confirm')}
                className="absolute right-2 top-1/2 -translate-y-1/2"
              />
            </div>
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
            )}
          </div>

          {/* Requisitos da senha */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Requisitos da nova senha:</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li className="flex items-center">
                <span className={`w-2 h-2 rounded-full mr-2 ${
                  formData.newPassword.length >= 6 ? 'bg-green-500' : 'bg-gray-300'
                }`}></span>
                Pelo menos 6 caracteres
              </li>
              <li className="flex items-center">
                <span className={`w-2 h-2 rounded-full mr-2 ${
                  /[a-z]/.test(formData.newPassword) ? 'bg-green-500' : 'bg-gray-300'
                }`}></span>
                Uma letra minúscula
              </li>
              <li className="flex items-center">
                <span className={`w-2 h-2 rounded-full mr-2 ${
                  /[A-Z]/.test(formData.newPassword) ? 'bg-green-500' : 'bg-gray-300'
                }`}></span>
                Uma letra maiúscula
              </li>
              <li className="flex items-center">
                <span className={`w-2 h-2 rounded-full mr-2 ${
                  /\d/.test(formData.newPassword) ? 'bg-green-500' : 'bg-gray-300'
                }`}></span>
                Um número
              </li>
            </ul>
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-3">
            <IconButton
              type="button"
              icon={X}
              variant="secondary"
              title="Cancelar"
              onClick={resetForm}
            />
            <IconButton
              type="submit"
              icon={isSubmitting ? SpinLoader : Save}
              variant="primary"
              disabled={isSubmitting}
              title={isSubmitting ? 'Alterando...' : 'Alterar senha'}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;